import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useLoginMutation } from "store/services/baseApi";
import { Divider } from "@mui/material";

function NavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isTransparent, setIsTransparent] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [{ isLoading: loggingUser }] = useLoginMutation();

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (location.pathname === "/") {
      const handleScroll = () => {
        const heroSection = document.getElementById("heroLanding");
        if (!heroSection) return;
        const heroHeight = heroSection.offsetHeight;
        setIsTransparent(window.scrollY < heroHeight * 0.8);
      };

      handleScroll();
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [location.pathname]);

  useEffect(() => {
    const sectionIds = ["heroLanding", "services", "meetTeam"];
    const observerOptions = { threshold: 0.5 };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    sectionIds.forEach((id) => {
      const section = document.getElementById(id);
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const navItems = [
    { label: "Discover Us", section: "heroLanding" },
    { label: "Our Services", section: "services" },
    { label: "Blogs", path: "/blogs" },
  ];

  const handleNavClick = (item) => {
    setDrawerOpen(false);
    if (item.path) {
      navigate(item.path);
    } else if (item.section) {
      if (location.pathname === "/home") {
        scrollToSection(item.section);
      } else {
        navigate("/home", { state: { scrollTo: item.section } });
      }
    }
  };

  const navButtons = navItems.flatMap((item, index) => [
    <Button
      key={item.label}
      onClick={() => handleNavClick(item)}
      sx={{
        color:
          location.pathname === "/home" && isTransparent ? "white" : "black",
        borderBottom:
          activeSection === item.section ? "2px solid crimson" : "none",
        transition: "all 0.3s ease",
      }}
    >
      {item.label}
    </Button>,
    index < navItems.length - 1 && (
      <Divider
        key={`divider-${item.label}`}
        orientation="vertical"
        flexItem
        sx={{ mx: 1, borderColor: "rgba(0, 0, 0, 0.2)" }}
      />
    ),
  ]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor:
            location.pathname === "/home" && isTransparent
              ? "transparent"
              : "#F8F8FF",
          boxShadow: "none",
          borderBottom:
            location.pathname === "/home" && isTransparent
              ? "none"
              : "1px solid #e0e0e0",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Link to="/home" style={{ textDecoration: "none" }}>
              <img
                src="/ncmLogo2.png"
                alt="Logo"
                style={{
                  height: 60,
                  cursor: "pointer",
                  filter:
                    location.pathname === "/home" && isTransparent
                      ? "brightness(0) invert(1)"
                      : "none",
                  transition: "filter 0.3s ease",
                }}
              />
            </Link>
          </Box>

          {isMobile ? (
            <IconButton
              edge="start"
              color={
                location.pathname === "/home" && isTransparent
                  ? "inherit"
                  : "default"
              }
              aria-label="menu"
              onClick={handleDrawerToggle}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {navButtons}
              <Link to="/tracking">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "crimson",
                    color: "white",
                    ml: 2,
                    "&:hover": {
                      backgroundColor: "darkred",
                    },
                  }}
                >
                  Track my order
                </Button>
              </Link>
              <Link to="/routine">
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "crimson",
                    color: "white",
                    ml: 2,
                    "&:hover": {
                      backgroundColor: "darkred",
                    },
                  }}
                >
                  Route Routine
                </Button>
              </Link>
              <Link to="/sign-in">
                <Button
                  variant="contained"
                  disabled={loggingUser}
                  sx={{
                    backgroundColor: "crimson",
                    color: "white",
                    ml: 2,
                    "&:hover": {
                      backgroundColor: "darkred",
                    },
                  }}
                >
                  Login
                </Button>
              </Link>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {isMobile && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          PaperProps={{
            sx: {
              height: "30vh",
              width: 200,
              top: "40px",
              backgroundColor: "#F8F8FF",
              borderRadius: "8px",
              transition: "transform 0.3s ease-in-out",
              overflow: "hidden",
              m: 2,
            },
          }}
        >
          <Box role="presentation" sx={{ width: "100%", height: "100%", p: 2 }}>
            <List>
              {navItems.map((item) => (
                <ListItem
                  button
                  key={item.label}
                  onClick={() => handleNavClick(item)}
                  sx={{
                    borderBottom:
                      activeSection === item.section
                        ? "2px solid crimson"
                        : "none",
                    transition: "background-color 0.3s",
                    "&:hover": {
                      backgroundColor: "rgba(220, 20, 60, 0.1)",
                      cursor: "pointer",
                    },
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
              <ListItem
                button
                component={Link}
                to="/sign-in"
                onClick={() => setDrawerOpen(false)}
                sx={{
                  backgroundColor: "crimson",
                  color: "white",
                  mt: 1,
                  borderRadius: 1,
                  transition: "background-color 0.3s",
                  "&:hover": {
                    backgroundColor: "darkred",
                  },
                }}
              >
                <ListItemText primary="Login" />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      )}
    </>
  );
}

export default NavBar;
